import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    fetchInstrumentDetails,
    fetchInstrumentQuote,
    fetchOrderMargins,
    fetchAccountOrders,
    fetchTradingSymbols,
    fetchTradingAccounts,
    submitTradingOrders,
    submitGTTOrders,
    cancelTradingOrder,
    cancelGttTrigger,
} from './api';
import {
    normalizeAccountOrdersResponse,
    orderSupportsCancellation,
    gttSupportsCancellation,
} from './ordersData';
import './TradingHubPage.css';
import GlassPaper from '../../components/GlassPaper';

const DEBOUNCE_MS = 250;

const VARIETY_OPTIONS = [
    { value: 'regular', description: 'Regular order' },
    { value: 'amo', description: 'After Market Order' },
    { value: 'co', description: 'Cover Order' },
    { value: 'iceberg', description: 'Iceberg Order' },
    { value: 'auction', description: 'Auction Order' },
];

const ORDER_TYPE_OPTIONS = [
    { value: 'MARKET', description: 'Market order' },
    { value: 'LIMIT', description: 'Limit order' },
    { value: 'SL', description: 'Stoploss order' },
    { value: 'SL-M', description: 'Stoploss-market order' },
];

const PRODUCT_OPTIONS = [
    { value: 'CNC', description: 'Cash & Carry for equity' },
    { value: 'NRML', description: 'Normal for futures and options' },
    { value: 'MIS', description: 'Margin Intraday Squareoff for futures and options' },
    { value: 'MTF', description: 'Margin Trading Facility' },
];

const VALIDITY_OPTIONS = [
    { value: 'DAY', description: 'Regular order' },
    { value: 'IOC', description: 'Immediate or Cancel' },
    { value: 'TTL', description: 'Order validity in minutes' },
];

const TRANSACTION_TYPE_OPTIONS = [
    { value: 'BUY', description: 'Buy' },
    { value: 'SELL', description: 'Sell' },
];

const GTT_ORDER_TYPE_OPTIONS = [
    { value: 'LIMIT', description: 'Limit' },
    { value: 'MARKET', description: 'Market' },
];

const INITIAL_GTT_LEGS = [
    {
        transactionType: 'SELL',
        orderType: 'LIMIT',
        triggerPrice: '',
        price: '',
    },
    {
        transactionType: 'BUY',
        orderType: 'LIMIT',
        triggerPrice: '',
        price: '',
    },
];

function formatExpiry(expiry) {
    if (!expiry) {
        return '—';
    }

    if (typeof expiry === 'string') {
        return expiry.slice(0, 10);
    }

    try {
        return expiry.toISOString().slice(0, 10);
    } catch (_err) {
        return String(expiry);
    }
}

function formatNumber(value, { minimumFractionDigits, maximumFractionDigits } = {}) {
    if (value == null || value === '') {
        return '—';
    }

    const number = Number(value);
    if (Number.isNaN(number)) {
        return String(value);
    }

    const isInteger = Number.isInteger(number);
    const minDigits =
        minimumFractionDigits != null
            ? minimumFractionDigits
            : isInteger
            ? 0
            : 2;
    const maxDigits =
        maximumFractionDigits != null
            ? maximumFractionDigits
            : isInteger
            ? 0
            : 2;

    return number.toLocaleString(undefined, {
        minimumFractionDigits: minDigits,
        maximumFractionDigits: maxDigits,
    });
}

function toInputString(value) {
    if (!Number.isFinite(value)) {
        return '';
    }

    const fixed = value.toFixed(6);
    return fixed.replace(/\.0+$|0+$/g, '').replace(/\.$/, '');
}

function isMultipleOfTick(value, tickSize) {
    if (!tickSize || !Number.isFinite(value)) {
        return true;
    }

    const multiplier = Math.round(value / tickSize);
    return Math.abs(value - multiplier * tickSize) < 1e-6;
}

function normalizeToTick(value, tickSize) {
    if (!tickSize || !Number.isFinite(value)) {
        return value;
    }

    const multiplier = Math.round(value / tickSize);
    const normalized = multiplier * tickSize;
    return Number(Number.isFinite(normalized) ? normalized.toFixed(6) : value);
}

function formatDateTime(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

const TradingHubPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedSymbol, setSelectedSymbol] = useState('');
    const [selectedInstrument, setSelectedInstrument] = useState(null);
    const [symbolsError, setSymbolsError] = useState('');
    const [instrumentError, setInstrumentError] = useState('');
    const [isFetchingSymbols, setIsFetchingSymbols] = useState(false);
    const [isFetchingInstrument, setIsFetchingInstrument] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [orderInputs, setOrderInputs] = useState({
        transactionType: TRANSACTION_TYPE_OPTIONS[0].value,
        variety: VARIETY_OPTIONS[0].value,
        orderType: ORDER_TYPE_OPTIONS[0].value,
        product: PRODUCT_OPTIONS[0].value,
        validity: VALIDITY_OPTIONS[0].value,
    });
    const [orderMode, setOrderMode] = useState('regular');
    const [gttInputs, setGttInputs] = useState({
        type: 'single',
        lastPrice: '',
        legs: INITIAL_GTT_LEGS.map((leg) => ({ ...leg })),
    });
    const [gttFormError, setGttFormError] = useState('');
    const [accountRows, setAccountRows] = useState([]);
    const [accountsError, setAccountsError] = useState('');
    const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [quoteData, setQuoteData] = useState(null);
    const [isFetchingQuote, setIsFetchingQuote] = useState(false);
    const [quoteError, setQuoteError] = useState('');
    const [isFetchingMargins, setIsFetchingMargins] = useState(false);
    const [marginError, setMarginError] = useState('');
    const [ordersData, setOrdersData] = useState([]);
    const [ordersError, setOrdersError] = useState('');
    const [ordersNotice, setOrdersNotice] = useState('');
    const [tagFilter, setTagFilter] = useState('');
    const [isFetchingOrders, setIsFetchingOrders] = useState(true);
    const [cancelDialog, setCancelDialog] = useState(null);
    const [isProcessingCancel, setIsProcessingCancel] = useState(false);
    const ordersFetcherRef = useRef(() => Promise.resolve());

    const marginInputSignature = useMemo(
        () =>
            JSON.stringify(
                accountRows.map((row) => ({
                    accountId: row.accountId,
                    quantity: row.quantity,
                    price: row.price,
                    triggerPrice: row.triggerPrice,
                    lots: row.lots,
                }))
            ),
        [accountRows]
    );

    const debounceTimeoutRef = useRef(null);
    const blurTimeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;
        setAccountsError('');
        setIsFetchingAccounts(true);

        fetchTradingAccounts()
            .then((accounts) => {
                if (isCancelled) {
                    return;
                }
                const rows = accounts.map((account) => ({
                    accountId: account.accountId,
                    alias: account.alias,
                    clientId: account.clientId,
                    broker: account.broker,
                    selected: false,
                    lots: '',
                    quantity: '',
                    quantityError: '',
                    price: '',
                    priceError: '',
                    triggerPrice: '',
                    triggerPriceError: '',
                    triggerPriceManuallyEdited: false,
                    totalMargin: null,
                    marginError: '',
                    status: '',
                }));
                setAccountRows(rows);
            })
            .catch((err) => {
                console.error('Failed to load trading accounts', err);
                if (!isCancelled) {
                    setAccountsError('Unable to load accounts. Please try again later.');
                    setAccountRows([]);
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsFetchingAccounts(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(async () => {
            setSymbolsError('');
            setIsFetchingSymbols(true);

            try {
                const symbolList = await fetchTradingSymbols(searchTerm.trim());
                if (!isCancelled) {
                    setSuggestions(symbolList);
                }
            } catch (err) {
                console.error('Failed to load trading symbols', err);
                if (!isCancelled) {
                    setSymbolsError('Unable to load symbols. Please try again.');
                    setSuggestions([]);
                }
            } finally {
                if (!isCancelled) {
                    setIsFetchingSymbols(false);
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            isCancelled = true;
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    useEffect(() => {
        if (orderMode !== 'regular') {
            setIsFetchingMargins(false);
            setMarginError('');
            setAccountRows((rows) =>
                rows.map((row) =>
                    row.totalMargin == null && !row.marginError
                        ? row
                        : { ...row, totalMargin: null, marginError: '' }
                )
            );
            return;
        }

        if (!selectedInstrument?.exchange || !selectedInstrument?.tradingsymbol) {
            setIsFetchingMargins(false);
            setMarginError('');
            setAccountRows((rows) =>
                rows.map((row) =>
                    row.totalMargin == null && !row.marginError
                        ? row
                        : { ...row, totalMargin: null, marginError: '' }
                )
            );
            return;
        }

        const normalizedAccounts = accountRows
            .map((row) => {
                const quantity = Number(row.quantity);
                if (!Number.isFinite(quantity) || quantity <= 0) {
                    return null;
                }
                const priceValue = row.price === '' || row.price == null ? null : Number(row.price);
                const triggerValue = row.triggerPrice === '' || row.triggerPrice == null ? null : Number(row.triggerPrice);
                return {
                    accountId: row.accountId,
                    quantity,
                    price: Number.isFinite(priceValue) ? priceValue : null,
                    triggerPrice: Number.isFinite(triggerValue) ? triggerValue : null,
                };
            })
            .filter(Boolean);

        if (!normalizedAccounts.length) {
            setIsFetchingMargins(false);
            setMarginError('');
            setAccountRows((rows) =>
                rows.map((row) =>
                    row.totalMargin == null && !row.marginError
                        ? row
                        : { ...row, totalMargin: null, marginError: '' }
                )
            );
            return;
        }

        let cancelled = false;

        const timeoutId = setTimeout(async () => {
            if (cancelled) {
                return;
            }

            setIsFetchingMargins(true);
            setMarginError('');

            try {
                const response = await fetchOrderMargins({
                    order: {
                        transactionType: orderInputs.transactionType,
                        variety: orderInputs.variety,
                        orderType: orderInputs.orderType,
                        product: orderInputs.product,
                    },
                    instrument: {
                        exchange: selectedInstrument.exchange,
                        tradingsymbol: selectedInstrument.tradingsymbol,
                    },
                    accounts: normalizedAccounts,
                });

                if (cancelled) {
                    return;
                }

                const results = Array.isArray(response?.results) ? response.results : [];
                const resultMap = new Map(results.map((item) => [Number(item.accountId), item]));
                let aggregatedError = '';

                setAccountRows((rows) =>
                    rows.map((row) => {
                        const quantity = Number(row.quantity);
                        if (!Number.isFinite(quantity) || quantity <= 0) {
                            return row.totalMargin == null && !row.marginError
                                ? row
                                : { ...row, totalMargin: null, marginError: '' };
                        }

                        const result = resultMap.get(Number(row.accountId));
                        if (!result) {
                            aggregatedError = aggregatedError || 'Margin data unavailable for some accounts.';
                            return { ...row, totalMargin: null, marginError: 'Not available' };
                        }

                        if (result.status === 'success') {
                            const marginValue = Number(result.totalMargin);
                            return {
                                ...row,
                                totalMargin: Number.isFinite(marginValue) ? marginValue : null,
                                marginError: '',
                            };
                        }

                        if (result.status === 'error') {
                            const errorMessage = result.error || 'Margin calculation failed';
                            aggregatedError = aggregatedError || errorMessage;
                            return { ...row, totalMargin: null, marginError: errorMessage };
                        }

                        return { ...row, totalMargin: null, marginError: '' };
                    })
                );

                setMarginError(aggregatedError);
            } catch (err) {
                if (cancelled) {
                    return;
                }
                console.error('Failed to calculate margins', err);
                setMarginError('Unable to calculate margins. Please try again.');
                setAccountRows((rows) =>
                    rows.map((row) =>
                        Number(row.quantity) > 0
                            ? { ...row, totalMargin: null, marginError: 'Calculation failed' }
                            : row
                    )
                );
            } finally {
                if (!cancelled) {
                    setIsFetchingMargins(false);
                }
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [
        orderMode,
        selectedInstrument?.exchange,
        selectedInstrument?.tradingsymbol,
        orderInputs.transactionType,
        orderInputs.variety,
        orderInputs.orderType,
        orderInputs.product,
        orderInputs.validity,
        marginInputSignature,
    ]);

    useEffect(() => {
        let cancelled = false;
        let intervalId;

        const fetchOrders = async (showLoading = false) => {
            if (cancelled) {
                return;
            }
            if (showLoading) {
                setIsFetchingOrders(true);
            }
            try {
                const response = await fetchAccountOrders();
                if (cancelled) {
                    return;
                }
                const results = normalizeAccountOrdersResponse(response?.results);
                setOrdersData(results);
                setOrdersError('');
            } catch (err) {
                if (cancelled) {
                    return;
                }
                console.error('Failed to fetch orders', err);
                setOrdersError('Unable to load orders or GTTs.');
                setOrdersNotice('');
            } finally {
                if (!cancelled) {
                    setIsFetchingOrders(false);
                }
            }
        };

        ordersFetcherRef.current = (showLoading = false) => fetchOrders(showLoading);
        fetchOrders(true);
        intervalId = setInterval(fetchOrders, 1000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
            ordersFetcherRef.current = () => Promise.resolve();
        };
    }, []);

    const resolveLotSize = () => {
        const size = Number(selectedInstrument?.lot_size);
        return Number.isFinite(size) && size > 0 ? size : null;
    };

    const resolveTickSize = () => {
        const size = Number(selectedInstrument?.tick_size);
        return Number.isFinite(size) && size > 0 ? size : null;
    };

    const closeCancelDialog = () => {
        if (!isProcessingCancel) {
            setCancelDialog(null);
        }
    };

    const promptCancelOrder = (account, order) => {
        if (!account || !order || !orderSupportsCancellation(order)) {
            return;
        }

        setCancelDialog({
            type: 'order',
            accountId: account.accountId,
            orderId: order.orderId,
            variety: order.variety,
            parentOrderId: order.parentOrderId,
            title: 'Cancel Order',
            message: `Do you really want to cancel order ${order.orderId}?`,
        });
    };

    const promptCancelGtt = (account, gtt) => {
        if (!account || !gtt || !gttSupportsCancellation(gtt)) {
            return;
        }

        setCancelDialog({
            type: 'gtt',
            accountId: account.accountId,
            triggerId: gtt.triggerId,
            title: 'Cancel GTT',
            message: `Do you really want to cancel GTT ${gtt.triggerId}?`,
        });
    };

    const handleConfirmCancellation = async () => {
        if (!cancelDialog) {
            return;
        }

        setIsProcessingCancel(true);
        try {
            if (cancelDialog.type === 'order') {
                await cancelTradingOrder({
                    accountId: cancelDialog.accountId,
                    orderId: cancelDialog.orderId,
                    variety: cancelDialog.variety,
                    parentOrderId: cancelDialog.parentOrderId,
                });
                setOrdersNotice(`Cancellation requested for order ${cancelDialog.orderId}.`);
            } else if (cancelDialog.type === 'gtt') {
                await cancelGttTrigger({
                    accountId: cancelDialog.accountId,
                    triggerId: cancelDialog.triggerId,
                });
                setOrdersNotice(`Cancellation requested for GTT ${cancelDialog.triggerId}.`);
            }
            setOrdersError('');
            await ordersFetcherRef.current(true);
        } catch (err) {
            console.error('Cancellation failed', err);
            const fallbackMessage =
                cancelDialog.type === 'order' ? 'Failed to cancel order.' : 'Failed to cancel GTT.';
            const errorMessage = err?.response?.data?.message || err?.message || fallbackMessage;
            setOrdersError(errorMessage);
            setOrdersNotice('');
        } finally {
            setIsProcessingCancel(false);
            setCancelDialog(null);
        }
    };

    useEffect(() => {
        setAccountRows((rows) => {
            const lotSizeValue = resolveLotSize();

            if (!lotSizeValue) {
                return rows.map((row) => ({ ...row, quantityError: '' }));
            }

            return rows.map((row) => {
                const lotsInt = row.lots === '' ? null : Number(row.lots);
                const quantityInt = row.quantity === '' ? null : Number(row.quantity);
                let nextQuantity = row.quantity;
                let quantityError = '';

                if (lotsInt != null && !Number.isNaN(lotsInt) && row.quantity === '') {
                    nextQuantity = String(lotsInt * lotSizeValue);
                }

                if (quantityInt != null && !Number.isNaN(quantityInt) && quantityInt % lotSizeValue !== 0) {
                    quantityError = `Qty must be a multiple of ${lotSizeValue}`;
                }

                return {
                    ...row,
                    quantity: nextQuantity,
                    quantityError,
                };
            });
        });
    }, [selectedInstrument]);

    useEffect(() => {
        setAccountRows((rows) => {
            const tickSizeValue = resolveTickSize();
            const tickSizeLabel = tickSizeValue ? toInputString(tickSizeValue) : '';

            return rows.map((row) => {
                let priceError = '';
                let triggerPriceError = row.triggerPriceError;
                let triggerPriceValue = row.triggerPrice;
                let triggerPriceManuallyEdited = row.triggerPriceManuallyEdited;
                let priceValue = row.price;

                if (!tickSizeValue) {
                    return {
                        ...row,
                        price: '',
                        priceError: '',
                        triggerPrice: '',
                        triggerPriceError: '',
                        triggerPriceManuallyEdited: false,
                    };
                }

                if (row.triggerPrice !== '') {
                    const triggerNumber = Number(row.triggerPrice);
                    if (!isMultipleOfTick(triggerNumber, tickSizeValue)) {
                        triggerPriceError = `Trigger price must be a multiple of ${tickSizeLabel}`;
                    } else {
                        triggerPriceError = '';
                        if (!row.price) {
                            const delta = orderInputs.transactionType === 'BUY' ? 10 : -10;
                            const candidate = normalizeToTick(Math.max(0, triggerNumber + delta), tickSizeValue);
                            priceValue = toInputString(candidate);
                            priceError = '';
                        }
                    }
                } else {
                    triggerPriceError = '';
                    priceValue = '';
                    priceError = '';
                }

                if (priceValue !== '') {
                    const priceNumber = Number(priceValue);
                    if (!isMultipleOfTick(priceNumber, tickSizeValue)) {
                        priceError = `Price must be a multiple of ${tickSizeLabel}`;
                    } else {
                        priceError = '';
                    }
                }

                return {
                    ...row,
                    price: priceValue,
                    priceError,
                    triggerPriceError,
                    triggerPrice: triggerPriceValue,
                    triggerPriceManuallyEdited,
                };
            });
        });
    }, [selectedInstrument]);

    const handleSelectSymbol = async (symbol) => {
        if (!symbol) {
            return;
        }

        setSubmitError('');
        setSelectedSymbol(symbol);
        setSearchTerm(symbol);
        setShowSuggestions(false);
        setInstrumentError('');
        setIsFetchingInstrument(true);
        setQuoteData(null);
        setQuoteError('');
        setMarginError('');
        setIsFetchingMargins(false);
        setAccountRows((rows) =>
            rows.map((row) => ({
                ...row,
                lots: '',
                quantity: '',
                quantityError: '',
                price: '',
                priceError: '',
                triggerPrice: '',
                triggerPriceError: '',
                triggerPriceManuallyEdited: false,
                totalMargin: null,
                marginError: '',
                status: '',
            }))
        );
        setGttInputs((prev) => ({
            type: prev.type,
            lastPrice: '',
            legs: INITIAL_GTT_LEGS.map((leg) => ({ ...leg })),
        }));
        setGttFormError('');

        try {
            const instrument = await fetchInstrumentDetails(symbol);
            setSelectedInstrument(instrument);

            if (instrument?.exchange && instrument?.tradingsymbol) {
                setIsFetchingQuote(true);
                try {
                    const quote = await fetchInstrumentQuote(instrument.exchange, instrument.tradingsymbol);
                    setQuoteData(quote);
                    setGttInputs((prev) => ({
                        ...prev,
                        lastPrice:
                            quote?.lastPrice != null && !Number.isNaN(Number(quote.lastPrice))
                                ? String(quote.lastPrice)
                                : prev.lastPrice,
                    }));
                } catch (err) {
                    console.error('Failed to load instrument quote', err);
                    setQuoteError('Unable to load latest quote details.');
                    setQuoteData(null);
                } finally {
                    setIsFetchingQuote(false);
                }
            }
        } catch (err) {
            console.error('Failed to load instrument details', err);
            setInstrumentError('Unable to load instrument details. Please try again.');
            setSelectedInstrument(null);
            setIsFetchingQuote(false);
            setQuoteData(null);
        } finally {
            setIsFetchingInstrument(false);
        }
    };

    const handleInputFocus = () => {
        setShowSuggestions(true);
    };

    const handleInputBlur = () => {
        blurTimeoutRef.current = setTimeout(() => {
            setShowSuggestions(false);
        }, 120);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSelectSymbol(searchTerm.trim());
        }
    };

    const handleOrderModeChange = (mode) => {
        if (mode === orderMode) {
            return;
        }
        setOrderMode(mode);
        setSubmitError('');
        if (mode === 'gtt') {
            setMarginError('');
            setIsFetchingMargins(false);
            setGttFormError('');
            if (quoteData?.lastPrice != null && !Number.isNaN(Number(quoteData.lastPrice))) {
                setGttInputs((prev) => ({
                    ...prev,
                    lastPrice: prev.lastPrice || String(quoteData.lastPrice),
                }));
            }
        } else {
            setGttFormError('');
        }
    };

    const handleGttFieldChange = (field, value) => {
        setGttFormError('');
        setGttInputs((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleGttLegFieldChange = (index, field, value) => {
        setGttFormError('');
        setGttInputs((prev) => {
            const legs = prev.legs.map((leg, legIndex) => {
                if (legIndex !== index) {
                    return leg;
                }
                const updated = { ...leg, [field]: value };
                if (field === 'orderType' && String(value).toUpperCase() === 'MARKET') {
                    updated.price = '';
                }
                return updated;
            });
            return {
                ...prev,
                legs,
            };
        });
    };

    const handleOrderInputChange = (field) => (event) => {
        const { value } = event.target;
        setSubmitError('');
        setMarginError('');
        setAccountRows((rows) =>
            rows.map((row) =>
                row.totalMargin == null && !row.marginError
                    ? row
                    : { ...row, totalMargin: null, marginError: '' }
            )
        );
        setOrderInputs((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAccountSelectChange = (accountId, isSelected) => {
        setSubmitError('');
        setMarginError('');
        setAccountRows((rows) =>
            rows.map((row) =>
                row.accountId === accountId
                    ? {
                          ...row,
                          selected: isSelected,
                      }
                    : row
            )
        );
    };

    const handleLotsChange = (accountId, rawValue) => {
        setSubmitError('');
        setMarginError('');
        setAccountRows((rows) => {
            const lotSizeValue = resolveLotSize();

            return rows.map((row) => {
                if (row.accountId !== accountId) {
                    return row;
                }

                if (rawValue === '') {
                    return {
                        ...row,
                        lots: '',
                        quantity: lotSizeValue ? '' : row.quantity,
                        quantityError: '',
                        totalMargin: null,
                        marginError: '',
                    };
                }

                const parsed = Number(rawValue);
                if (Number.isNaN(parsed)) {
                    return row;
                }

                const sanitized = Math.max(0, Math.floor(parsed));
                const lotsStr = String(sanitized);

                if (!lotSizeValue) {
                    return {
                        ...row,
                        lots: lotsStr,
                        totalMargin: null,
                        marginError: '',
                    };
                }

                return {
                    ...row,
                    lots: lotsStr,
                    quantity: String(sanitized * lotSizeValue),
                    quantityError: '',
                    totalMargin: null,
                    marginError: '',
                };
            });
        });
    };

    const handleQuantityChange = (accountId, rawValue) => {
        setSubmitError('');
        setMarginError('');
        setAccountRows((rows) => {
            const lotSizeValue = resolveLotSize();

            return rows.map((row) => {
                if (row.accountId !== accountId) {
                    return row;
                }

                if (rawValue === '') {
                    return {
                        ...row,
                        quantity: '',
                        quantityError: '',
                        lots: lotSizeValue ? '' : row.lots,
                        totalMargin: null,
                        marginError: '',
                    };
                }

                const parsed = Number(rawValue);
                if (Number.isNaN(parsed)) {
                    return row;
                }

                const sanitized = Math.max(0, Math.floor(parsed));
                const quantityStr = String(sanitized);

                if (!lotSizeValue) {
                    return {
                        ...row,
                        quantity: quantityStr,
                        quantityError: '',
                        totalMargin: null,
                        marginError: '',
                    };
                }

                const hasError = sanitized % lotSizeValue !== 0;
                return {
                    ...row,
                    quantity: quantityStr,
                    quantityError: hasError ? `Qty must be a multiple of ${lotSizeValue}` : '',
                    lots: hasError ? row.lots : String(Math.floor(sanitized / lotSizeValue)),
                    totalMargin: null,
                    marginError: '',
                };
            });
        });
    };

    const handlePriceChange = (accountId, rawValue) => {
        setSubmitError('');
        setMarginError('');
        setAccountRows((rows) => {
            const tickSizeValue = resolveTickSize();
            const tickSizeLabel = tickSizeValue ? toInputString(tickSizeValue) : '';

            return rows.map((row) => {
                if (row.accountId !== accountId) {
                    return row;
                }

                if (rawValue === '') {
                    return {
                        ...row,
                        price: '',
                        priceError: '',
                        triggerPrice: row.triggerPrice,
                        triggerPriceError: row.triggerPriceError,
                        triggerPriceManuallyEdited: row.triggerPriceManuallyEdited,
                        totalMargin: null,
                        marginError: '',
                    };
                }

                const parsed = Number(rawValue);
                if (!Number.isFinite(parsed)) {
                    return row;
                }

                const sanitized = Math.max(0, parsed);
                const priceStr = toInputString(sanitized);

                let priceError = '';
                if (tickSizeValue && !isMultipleOfTick(sanitized, tickSizeValue)) {
                    priceError = `Price must be a multiple of ${tickSizeLabel}`;
                }

                return {
                    ...row,
                    price: priceStr,
                    priceError,
                    triggerPrice: row.triggerPrice,
                    triggerPriceError: row.triggerPriceError,
                    triggerPriceManuallyEdited: row.triggerPriceManuallyEdited,
                    totalMargin: null,
                    marginError: '',
                };
            });
        });
    };

    const handleTriggerPriceChange = (accountId, rawValue) => {
        setSubmitError('');
        setMarginError('');
        setAccountRows((rows) => {
            const tickSizeValue = resolveTickSize();
            const tickSizeLabel = tickSizeValue ? toInputString(tickSizeValue) : '';

            return rows.map((row) => {
                if (row.accountId !== accountId) {
                    return row;
                }

                if (rawValue === '') {
                    return {
                        ...row,
                        triggerPrice: '',
                        triggerPriceError: '',
                        triggerPriceManuallyEdited: false,
                        price: '',
                        priceError: '',
                        totalMargin: null,
                        marginError: '',
                    };
                }

                const parsed = Number(rawValue);
                if (!Number.isFinite(parsed)) {
                    return row;
                }

                const sanitized = Math.max(0, parsed);
                const triggerPriceStr = toInputString(sanitized);

                let triggerPriceError = '';
                if (tickSizeValue && !isMultipleOfTick(sanitized, tickSizeValue)) {
                    triggerPriceError = `Trigger price must be a multiple of ${tickSizeLabel}`;
                }

                let priceValue = row.price;
                let priceError = row.priceError;

                if (tickSizeValue && triggerPriceError === '') {
                    const delta = orderInputs.transactionType === 'BUY' ? 10 : -10;
                    const candidate = normalizeToTick(Math.max(0, sanitized + delta), tickSizeValue);
                    priceValue = toInputString(candidate);
                }

                if (priceValue !== '') {
                    const priceNumber = Number(priceValue);
                    if (tickSizeValue && !isMultipleOfTick(priceNumber, tickSizeValue)) {
                        priceError = `Price must be a multiple of ${tickSizeLabel}`;
                    } else {
                        priceError = '';
                    }
                }

                return {
                    ...row,
                    triggerPrice: triggerPriceStr,
                    triggerPriceError,
                    triggerPriceManuallyEdited: true,
                    price: priceValue,
                    priceError,
                    totalMargin: null,
                    marginError: '',
                };
            });
        });
    };

    const handleSubmit = async () => {
        if (isSubmitting) {
            return;
        }

        if (!selectedInstrument) {
            setSubmitError('Select a symbol before submitting orders.');
            return;
        }

        const selectedAccounts = accountRows.filter((row) => row.selected);

        if (!selectedAccounts.length) {
            setSubmitError('Select at least one account to trade.');
            return;
        }

        const hasInvalidQuantity = selectedAccounts.some((row) => {
            const qty = Number(row.quantity);
            return !Number.isFinite(qty) || qty <= 0;
        });

        if (hasInvalidQuantity) {
            setSubmitError('Enter a valid quantity for each selected account.');
            return;
        }

        if (hasQuantityError) {
            setSubmitError('Resolve quantity validation errors before submitting.');
            return;
        }

        if (orderMode === 'gtt') {
            const lastPriceNumber = Number(gttInputs.lastPrice);
            if (!Number.isFinite(lastPriceNumber) || lastPriceNumber <= 0) {
                setGttFormError('Provide a valid last price before submitting.');
                return;
            }

            const legIndexes = gttInputs.type === 'two-leg' ? [0, 1] : [0];
            const legsPayload = [];
            for (const index of legIndexes) {
                const leg = gttInputs.legs[index];
                const transactionType = String(leg.transactionType || '').toUpperCase();
                if (!['BUY', 'SELL'].includes(transactionType)) {
                    setGttFormError('Select transaction types for each leg.');
                    return;
                }
                const orderType = String(leg.orderType || '').toUpperCase();
                if (!['LIMIT', 'MARKET'].includes(orderType)) {
                    setGttFormError('Select valid order types for each leg.');
                    return;
                }
                const triggerPriceNumber = Number(leg.triggerPrice);
                if (!Number.isFinite(triggerPriceNumber)) {
                    setGttFormError('Enter trigger prices for each leg.');
                    return;
                }
                let priceNumber = Number(leg.price);
                if (orderType === 'LIMIT') {
                    if (!Number.isFinite(priceNumber)) {
                        setGttFormError('Enter limit prices for each leg.');
                        return;
                    }
                } else {
                    priceNumber = 0;
                }
                legsPayload.push({
                    transactionType,
                    orderType,
                    triggerPrice: triggerPriceNumber,
                    price: priceNumber,
                });
            }

            setSubmitError('');
            setGttFormError('');
            setIsSubmitting(true);
            setAccountRows((rows) =>
                rows.map((row) =>
                    row.selected
                        ? {
                              ...row,
                              status: 'Submitting…',
                          }
                        : row
                )
            );

            const payload = {
                instrument: {
                    exchange: selectedInstrument.exchange,
                    tradingsymbol: selectedInstrument.tradingsymbol,
                },
                gtt: {
                    type: gttInputs.type === 'two-leg' ? 'two-leg' : 'single',
                    lastPrice: lastPriceNumber,
                    product: orderInputs.product,
                    legs: legsPayload,
                },
                accounts: selectedAccounts.map((row) => ({
                    accountId: row.accountId,
                    quantity: Number(row.quantity),
                })),
            };

            try {
                const response = await submitGTTOrders(payload);
                const resultArray = Array.isArray(response?.results) ? response.results : [];
                const resultMap = new Map(resultArray.map((entry) => [Number(entry.accountId), entry]));

                setAccountRows((rows) =>
                    rows.map((row) => {
                        if (!row.selected) {
                            return row;
                        }

                        const result = resultMap.get(Number(row.accountId));
                        if (!result) {
                            return {
                                ...row,
                                status: 'No response',
                            };
                        }

                        if (result.status === 'success') {
                            const triggerIdLabel = result.triggerId ? ` (Trigger ${result.triggerId})` : '';
                            return {
                                ...row,
                                status: `Success${triggerIdLabel}`,
                            };
                        }

                        return {
                            ...row,
                            status: `Error: ${result.error || 'GTT failed'}`,
                        };
                    })
                );
            } catch (err) {
                console.error('GTT submission failed', err);
                setGttFormError('Failed to place GTT. Please try again.');
                setAccountRows((rows) =>
                    rows.map((row) =>
                        row.selected
                            ? {
                                  ...row,
                                  status: 'Error: Submit failed',
                              }
                            : row
                    )
                );
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        if (hasQuantityError || hasPriceError) {
            setSubmitError('Resolve validation errors before submitting.');
            return;
        }

        setSubmitError('');
        setIsSubmitting(true);
        setAccountRows((rows) =>
            rows.map((row) =>
                row.selected
                    ? {
                          ...row,
                          status: 'Submitting…',
                      }
                    : row
            )
        );

        const payload = {
            order: {
                transactionType: orderInputs.transactionType,
                variety: orderInputs.variety,
                orderType: orderInputs.orderType,
                product: orderInputs.product,
                validity: orderInputs.validity,
            },
            instrument: selectedInstrument,
            accounts: selectedAccounts.map((row) => ({
                accountId: row.accountId,
                quantity: Number(row.quantity),
                price: row.price === '' ? null : Number(row.price),
                triggerPrice: row.triggerPrice === '' ? null : Number(row.triggerPrice),
            })),
        };

        try {
            const response = await submitTradingOrders(payload);
            const resultArray = Array.isArray(response?.results) ? response.results : [];
            const resultMap = new Map(resultArray.map((entry) => [Number(entry.accountId), entry]));

            setAccountRows((rows) =>
                rows.map((row) => {
                    if (!row.selected) {
                        return row;
                    }

                    const result = resultMap.get(Number(row.accountId));
                    if (!result) {
                        return {
                            ...row,
                            status: 'No response',
                        };
                    }

                    if (result.status === 'success') {
                        const orderIdLabel = result.orderId ? ` (Order ${result.orderId})` : '';
                        return {
                            ...row,
                            status: `Success${orderIdLabel}`,
                        };
                    }

                    return {
                        ...row,
                        status: `Error: ${result.error || 'Order failed'}`,
                    };
                })
            );
        } catch (err) {
            console.error('Order submission failed', err);
            setSubmitError('Failed to submit orders. Please try again.');
            setAccountRows((rows) =>
                rows.map((row) =>
                    row.selected
                        ? {
                              ...row,
                              status: 'Error: Submit failed',
                          }
                        : row
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const lotSizeValue = resolveLotSize();
    const tickSizeValue = resolveTickSize();
    const hasQuantityError = accountRows.some((row) => Boolean(row.quantityError));
    const hasPriceError = accountRows.some((row) => Boolean(row.priceError) || Boolean(row.triggerPriceError));
    const selectedAccountsList = accountRows.filter((row) => row.selected);
    const anyAccountSelected = selectedAccountsList.length > 0;
    const hasInvalidSelectedQuantities = selectedAccountsList.some((row) => {
        const qty = Number(row.quantity);
        return !Number.isFinite(qty) || qty <= 0;
    });
    const isSubmitDisabled =
        orderMode === 'gtt'
            ? isSubmitting || !selectedInstrument || !anyAccountSelected || hasInvalidSelectedQuantities || !accountRows.length
            : isSubmitting || !selectedInstrument || !anyAccountSelected || hasQuantityError || hasPriceError || !accountRows.length;

    const tableColumnCount = orderMode === 'regular' ? 10 : 7;
    const normalizedTagFilter = tagFilter.trim().toLowerCase();
    const hasTagFilter = Boolean(normalizedTagFilter);
    const visibleOrderCards = ordersData.filter((account) => {
        const hasOrders = Array.isArray(account.orders) && account.orders.length > 0;
        const hasGtts = Array.isArray(account.gtts) && account.gtts.length > 0;
        return account.status === 'error' || hasOrders || hasGtts;
    });

    const renderSuggestions = () => {
        if (!showSuggestions) {
            return null;
        }

        if (symbolsError) {
            return (
                <div className="symbol-search__dropdown-item symbol-search__dropdown-item--message">
                    {symbolsError}
                </div>
            );
        }

        if (isFetchingSymbols) {
            return (
                <div className="symbol-search__dropdown-item symbol-search__dropdown-item--message">
                    Loading symbols…
                </div>
            );
        }

        if (!suggestions.length) {
            return (
                <div className="symbol-search__dropdown-item symbol-search__dropdown-item--message">
                    No symbols found
                </div>
            );
        }

        return suggestions.map(({ symbol, exchange, name, strike, instrumentType }) => (
            <button
                key={symbol}
                type="button"
                className="symbol-search__dropdown-item"
                onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelectSymbol(symbol);
                }}
            >
                <span className="symbol-search__dropdown-item-symbol">{symbol}</span>
                <span className="symbol-search__dropdown-item-meta">
                    {name && <span className="symbol-search__meta-item">{name}</span>}
                    {instrumentType && (
                        <span className="symbol-search__meta-item symbol-search__meta-item--badge">
                            {instrumentType}
                        </span>
                    )}
                    {strike != null && (
                        <span className="symbol-search__meta-item">{formatNumber(Number(strike))}</span>
                    )}
                    <span className="symbol-search__meta-item symbol-search__meta-item--exchange">
                        {exchange || '—'}
                    </span>
                </span>
            </button>
        ));
    };

    const instrumentDetails = selectedInstrument
        ? [
              { label: 'Name', value: selectedInstrument.name || '—' },
              { label: 'Expiry', value: formatExpiry(selectedInstrument.expiry) },
              { label: 'Strike', value: formatNumber(selectedInstrument.strike) },
              { label: 'Lot Size', value: formatNumber(selectedInstrument.lot_size) },
              { label: 'Instrument Type', value: selectedInstrument.instrument_type || '—' },
              { label: 'Tick Size', value: formatNumber(selectedInstrument.tick_size) },
              { label: 'Segment', value: selectedInstrument.segment || '—' },
              { label: 'Exchange', value: selectedInstrument.exchange || '—' },
          ]
        : [];

    const quoteDetails = selectedInstrument
        ? [
              {
                  label: 'Last Price',
                  value: formatNumber(quoteData?.lastPrice, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                  }),
              },
              { label: 'Last Trade Time', value: formatDateTime(quoteData?.lastTradeTime) },
              {
                  label: 'Last Qty',
                  value: formatNumber(quoteData?.lastQuantity, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                  }),
              },
              {
                  label: 'Open Buy Qty',
                  value: formatNumber(quoteData?.buyQuantity, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                  }),
              },
              {
                  label: 'Open Sell Qty',
                  value: formatNumber(quoteData?.sellQuantity, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                  }),
              },
              {
                  label: 'Volume',
                  value: formatNumber(quoteData?.volume, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                  }),
              },
              {
                  label: 'Open Interest',
                  value: formatNumber(quoteData?.openInterest, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                  }),
              },
          ]
        : [];

    const symbolDetails = selectedInstrument ? [...instrumentDetails, ...quoteDetails] : [];

    return (
        <div className="trading-hub">
            <GlassPaper
                component="section"
                className="trading-hub__section trading-hub__section--symbol"
                elevation={0}
                square={false}
            >
                <header className="trading-hub__header">
                    <h2>Symbol</h2>
                    <p>Select a trading symbol to view instrument details.</p>
                </header>
                <div className="symbol-search">
                    <label htmlFor="trading-hub-symbol" className="symbol-search__label">
                        Symbol
                    </label>
                    <div className="symbol-search__input-wrapper">
                        <input
                            id="trading-hub-symbol"
                            type="text"
                            className="symbol-search__input"
                            value={searchTerm}
                            placeholder="Start typing a symbol"
                            onChange={(event) => {
                                const value = event.target.value;
                                setSearchTerm(value);
                                setShowSuggestions(true);
                                if (selectedSymbol && value !== selectedSymbol) {
                                    setSelectedSymbol('');
                                }
                                if (selectedInstrument && value !== selectedSymbol) {
                                    setSelectedInstrument(null);
                                }
                                if (instrumentError) {
                                    setInstrumentError('');
                                }
                                setQuoteData(null);
                                setQuoteError('');
                            }}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={handleKeyDown}
                        />
                        <div className={`symbol-search__dropdown ${showSuggestions ? 'symbol-search__dropdown--visible' : ''}`}>
                            {renderSuggestions()}
                        </div>
                    </div>
                </div>
                {instrumentError && <div className="trading-hub__error">{instrumentError}</div>}
                <div className="symbol-details">
                    {isFetchingInstrument && (
                        <div className="symbol-details__message">Loading instrument details…</div>
                    )}
                    {!isFetchingInstrument && !selectedInstrument && (
                        <div className="symbol-details__message">
                            {selectedSymbol
                                ? 'No instrument details available for the selected symbol.'
                                : 'Instrument details will appear here once a symbol is selected.'}
                        </div>
                    )}
                    {!isFetchingInstrument && selectedInstrument && (
                        <>
                            {isFetchingQuote && (
                                <div className="symbol-details__message">Loading latest quote…</div>
                            )}
                            {quoteError && (
                                <div className="symbol-details__message symbol-details__message--error">{quoteError}</div>
                            )}
                            <dl className="symbol-details__grid">
                                {symbolDetails.map(({ label, value }) => (
                                    <div key={label} className="symbol-details__item">
                                        <dt>{label}</dt>
                                        <dd>{value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </>
                    )}
                </div>
            </GlassPaper>

            <GlassPaper
                component="section"
                className="trading-hub__section trading-hub__section--trade"
                elevation={0}
                square={false}
            >
                <header className="trading-hub__header">
                    <h2>Trade</h2>
                    <p>Set order preferences and target the accounts that should receive this trade.</p>
                </header>

                <div className="trade-mode-toggle btn-group btn-group-sm mb-3" role="group">
                    <button
                        type="button"
                        className={`btn btn-sm btn-${orderMode === 'regular' ? 'primary' : 'secondary'}`}
                        onClick={() => handleOrderModeChange('regular')}
                    >
                        Regular Order
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm btn-${orderMode === 'gtt' ? 'primary' : 'secondary'}`}
                        onClick={() => handleOrderModeChange('gtt')}
                    >
                        GTT
                    </button>
                </div>

                {orderMode === 'regular' ? (
                    <div className="trade-controls">
                        <div className="trade-control">
                            <label htmlFor="trade-transaction-type">Transaction Type</label>
                            <select
                                id="trade-transaction-type"
                                value={orderInputs.transactionType}
                                onChange={handleOrderInputChange('transactionType')}
                            >
                                {TRANSACTION_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {`${option.value} — ${option.description}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="trade-control">
                            <label htmlFor="trade-variety">Variety</label>
                            <select
                                id="trade-variety"
                                value={orderInputs.variety}
                                onChange={handleOrderInputChange('variety')}
                            >
                                {VARIETY_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {`${option.value} — ${option.description}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="trade-control">
                            <label htmlFor="trade-order-type">Order Type</label>
                            <select
                                id="trade-order-type"
                                value={orderInputs.orderType}
                                onChange={handleOrderInputChange('orderType')}
                            >
                                {ORDER_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {`${option.value} — ${option.description}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="trade-control">
                            <label htmlFor="trade-product">Product</label>
                            <select
                                id="trade-product"
                                value={orderInputs.product}
                                onChange={handleOrderInputChange('product')}
                            >
                                {PRODUCT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {`${option.value} — ${option.description}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="trade-control">
                            <label htmlFor="trade-validity">Validity</label>
                            <select
                                id="trade-validity"
                                value={orderInputs.validity}
                                onChange={handleOrderInputChange('validity')}
                            >
                                {VALIDITY_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {`${option.value} — ${option.description}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="gtt-controls">
                            <div className="gtt-control">
                                <label htmlFor="gtt-type">GTT Type</label>
                                <select
                                    id="gtt-type"
                                    value={gttInputs.type}
                                    onChange={(event) => handleGttFieldChange('type', event.target.value)}
                                >
                                    <option value="single">Single-leg</option>
                                    <option value="two-leg">OCO (Two-leg)</option>
                                </select>
                            </div>
                            <div className="gtt-control">
                                <label htmlFor="gtt-last-price">Last Price</label>
                                <input
                                    id="gtt-last-price"
                                    type="number"
                                    step="0.01"
                                    value={gttInputs.lastPrice}
                                    onChange={(event) => handleGttFieldChange('lastPrice', event.target.value)}
                                />
                            </div>
                            <div className="gtt-control">
                                <label htmlFor="gtt-product">Product</label>
                                <select
                                    id="gtt-product"
                                    value={orderInputs.product}
                                    onChange={handleOrderInputChange('product')}
                                >
                                    {PRODUCT_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {`${option.value} — ${option.description}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="gtt-legs">
                            <div className="gtt-leg">
                                <div className="gtt-leg__title">Leg 1</div>
                                <div className="gtt-leg-grid">
                                    <div className="gtt-control">
                                        <label>Transaction Type</label>
                                        <select
                                            value={gttInputs.legs[0].transactionType}
                                            onChange={(event) => handleGttLegFieldChange(0, 'transactionType', event.target.value)}
                                        >
                                            {TRANSACTION_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.value}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="gtt-control">
                                        <label>Order Type</label>
                                        <select
                                            value={gttInputs.legs[0].orderType}
                                            onChange={(event) => handleGttLegFieldChange(0, 'orderType', event.target.value)}
                                        >
                                            {GTT_ORDER_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.value}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="gtt-control">
                                        <label>Trigger Price</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={gttInputs.legs[0].triggerPrice}
                                            onChange={(event) => handleGttLegFieldChange(0, 'triggerPrice', event.target.value)}
                                        />
                                    </div>
                                    <div className="gtt-control">
                                        <label>Price</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={gttInputs.legs[0].price}
                                            onChange={(event) => handleGttLegFieldChange(0, 'price', event.target.value)}
                                            disabled={String(gttInputs.legs[0].orderType).toUpperCase() === 'MARKET'}
                                        />
                                    </div>
                                </div>
                            </div>
                            {gttInputs.type === 'two-leg' && (
                                <div className="gtt-leg">
                                    <div className="gtt-leg__title">Leg 2</div>
                                    <div className="gtt-leg-grid">
                                        <div className="gtt-control">
                                            <label>Transaction Type</label>
                                            <select
                                                value={gttInputs.legs[1].transactionType}
                                                onChange={(event) => handleGttLegFieldChange(1, 'transactionType', event.target.value)}
                                            >
                                                {TRANSACTION_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.value}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="gtt-control">
                                            <label>Order Type</label>
                                            <select
                                                value={gttInputs.legs[1].orderType}
                                                onChange={(event) => handleGttLegFieldChange(1, 'orderType', event.target.value)}
                                            >
                                                {GTT_ORDER_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.value}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="gtt-control">
                                            <label>Trigger Price</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={gttInputs.legs[1].triggerPrice}
                                                onChange={(event) => handleGttLegFieldChange(1, 'triggerPrice', event.target.value)}
                                            />
                                        </div>
                                        <div className="gtt-control">
                                            <label>Price</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={gttInputs.legs[1].price}
                                                onChange={(event) => handleGttLegFieldChange(1, 'price', event.target.value)}
                                                disabled={String(gttInputs.legs[1].orderType).toUpperCase() === 'MARKET'}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
                <div className="trade-table-wrapper">
                    <table className="trade-table">
                        <thead>
                            <tr>
                                <th>Select</th>
                                <th>Account Alias</th>
                                <th>Client ID</th>
                                <th>Broker</th>
                                <th>Lots</th>
                                <th>Qty</th>
                                {orderMode === 'regular' && <th>Trigger Price</th>}
                                {orderMode === 'regular' && <th>Price</th>}
                                {orderMode === 'regular' && <th>Total Margin</th>}
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isFetchingAccounts && (
                                <tr>
                                    <td colSpan={tableColumnCount} className="trade-table__message">
                                        Loading accounts…
                                    </td>
                                </tr>
                            )}
                            {!isFetchingAccounts && accountsError && (
                                <tr>
                                    <td colSpan={tableColumnCount} className="trade-table__message trade-table__message--error">
                                        {accountsError}
                                    </td>
                                </tr>
                            )}
                            {!isFetchingAccounts && !accountsError && !accountRows.length && (
                                <tr>
                                    <td colSpan={tableColumnCount} className="trade-table__message">
                                        No enabled accounts available.
                                    </td>
                                </tr>
                            )}
                            {!isFetchingAccounts && !accountsError &&
                                accountRows.map((row) => (
                                    <tr key={row.accountId}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={row.selected}
                                                onChange={(event) =>
                                                    handleAccountSelectChange(row.accountId, event.target.checked)
                                                }
                                            />
                                        </td>
                                        <td>{row.alias || '—'}</td>
                                        <td>{row.clientId || '—'}</td>
                                        <td>{row.broker || '—'}</td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={row.lots}
                                                className="trade-table__input"
                                                inputMode="numeric"
                                                onChange={(event) =>
                                                    handleLotsChange(row.accountId, event.target.value)
                                                }
                                            />
                                        </td>
                                        <td>
                                            <div className="trade-table__input-wrapper">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step={lotSizeValue || 1}
                                                    value={row.quantity}
                                                    className={`trade-table__input ${row.quantityError ? 'trade-table__input--error' : ''}`}
                                                    inputMode="numeric"
                                                    onChange={(event) =>
                                                        handleQuantityChange(row.accountId, event.target.value)
                                                    }
                                                    disabled={!lotSizeValue}
                                                    placeholder={lotSizeValue ? undefined : 'Select a symbol'}
                                                />
                                                {row.quantityError && (
                                                    <div className="trade-table__validation">{row.quantityError}</div>
                                                )}
                                            </div>
                                        </td>
                                        {orderMode === 'regular' && (
                                            <td>
                                                <div className="trade-table__input-wrapper">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step={tickSizeValue || 0.01}
                                                        value={row.triggerPrice}
                                                        className={`trade-table__input ${row.triggerPriceError ? 'trade-table__input--error' : ''}`}
                                                        inputMode="decimal"
                                                        onChange={(event) =>
                                                            handleTriggerPriceChange(row.accountId, event.target.value)
                                                        }
                                                        disabled={!tickSizeValue}
                                                        placeholder={tickSizeValue ? undefined : 'Select a symbol'}
                                                    />
                                                    {row.triggerPriceError && (
                                                        <div className="trade-table__validation">{row.triggerPriceError}</div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {orderMode === 'regular' && (
                                            <td>
                                                <div className="trade-table__input-wrapper">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step={tickSizeValue || 0.01}
                                                        value={row.price}
                                                        className={`trade-table__input ${row.priceError ? 'trade-table__input--error' : ''}`}
                                                        inputMode="decimal"
                                                        onChange={(event) =>
                                                            handlePriceChange(row.accountId, event.target.value)
                                                        }
                                                        disabled={!tickSizeValue}
                                                        placeholder={tickSizeValue ? undefined : 'Select a symbol'}
                                                    />
                                                    {row.priceError && (
                                                        <div className="trade-table__validation">{row.priceError}</div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {orderMode === 'regular' && (
                                            <td className="trade-table__cell--numeric">
                                                {row.marginError ? (
                                                    <span className="trade-table__validation">{row.marginError}</span>
                                                ) : (
                                                    <span>
                                                        {isFetchingMargins && Number(row.quantity) > 0
                                                            ? 'Calculating…'
                                                            : formatNumber(row.totalMargin, {
                                                                  minimumFractionDigits: 2,
                                                                  maximumFractionDigits: 2,
                                                              })}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td>{row.status || '—'}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                <div className="trade-hints">
                    {orderMode === 'regular' ? (
                        <>
                            {(!lotSizeValue || !tickSizeValue) && (
                                <div className="trade-hint">Select a symbol to enable lot and tick-sized inputs.</div>
                            )}
                            {hasQuantityError && (
                                <div className="trade-hint trade-hint--error">
                                    Resolve quantity validation errors before submitting the order.
                                </div>
                            )}
                            {hasPriceError && (
                                <div className="trade-hint trade-hint--error">
                                    Resolve price and trigger price validation errors before submitting the order.
                                </div>
                            )}
                            {isFetchingMargins && (
                                <div className="trade-hint">Calculating margins…</div>
                            )}
                            {marginError && (
                                <div className="trade-hint trade-hint--error">{marginError}</div>
                            )}
                        </>
                    ) : (
                        <>
                            {hasInvalidSelectedQuantities && (
                                <div className="trade-hint trade-hint--error">Enter a valid quantity for each selected account.</div>
                            )}
                            {gttFormError && (
                                <div className="trade-hint trade-hint--error">{gttFormError}</div>
                            )}
                        </>
                    )}
                    {submitError && <div className="trade-hint trade-hint--error">{submitError}</div>}
                </div>

                <div className="trade-actions">
                    <button
                        type="button"
                        className="btn btn-primary trade-submit-btn"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                    >
                        {isSubmitting ? 'Submitting…' : 'Submit'}
                    </button>
                </div>
            </GlassPaper>
            <GlassPaper
                component="section"
                className="trading-hub__section trading-hub__section--orders"
                elevation={0}
                square={false}
            >
                <header className="trading-hub__header">
                    <div className="trading-hub__header-content">
                        <h2>Orders &amp; GTTs</h2>
                        <p>Review intraday orders and active triggers across enabled accounts.</p>
                    </div>
                    <div className="orders-filter">
                        <label htmlFor="orders-tag-filter" className="orders-filter__label">
                            Tag filter
                        </label>
                        <input
                            id="orders-tag-filter"
                            type="search"
                            className="orders-filter__input"
                            placeholder="Filter orders by tag"
                            value={tagFilter}
                            onChange={(event) => setTagFilter(event.target.value)}
                        />
                    </div>
                </header>

                {ordersError && <div className="trading-hub__error">{ordersError}</div>}
                {!ordersError && ordersNotice && <div className="trading-hub__notice">{ordersNotice}</div>}

                <div className="orders-grid">
                    {isFetchingOrders && !visibleOrderCards.length ? (
                        <div className="orders-card orders-card--message">Loading orders…</div>
                    ) : visibleOrderCards.length ? (
                        visibleOrderCards.map((account) => {
                            const hasOrders = account.orders.length > 0;
                            const hasGtts = account.gtts.length > 0;
                            const filteredOrders = hasTagFilter
                                ? account.orders.filter((order) => {
                                      const tagValue = order.tag == null ? '' : String(order.tag);
                                      return tagValue.toLowerCase().includes(normalizedTagFilter);
                                  })
                                : account.orders;
                            const showFilteredEmptyState = hasTagFilter && hasOrders && !filteredOrders.length;

                            return (
                                <div key={account.accountId} className="orders-card">
                                    <div className="orders-card__header">
                                        <div className="orders-card__title">{account.alias || '—'}</div>
                                        <div className="orders-card__meta">
                                            <span>{account.clientId || '—'}</span>
                                            <span>{account.broker || '—'}</span>
                                        </div>
                                    </div>
                                    {account.status === 'error' ? (
                                        <div className="orders-card__message orders-card__message--error">
                                            {account.error || 'Unable to load orders or GTTs.'}
                                        </div>
                                    ) : (
                                        <>
                                            {hasOrders && (
                                                <div className="orders-card__section">
                                                    <h3 className="orders-card__subtitle">Regular Orders</h3>
                                                    {showFilteredEmptyState ? (
                                                        <div className="orders-card__message">
                                                            No orders match this tag filter.
                                                        </div>
                                                    ) : (
                                                        <div className="orders-table-wrapper">
                                                            <table className="orders-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Order ID</th>
                                                                        <th>Symbol</th>
                                                                        <th>Status</th>
                                                                        <th>Txn</th>
                                                                        <th>Type</th>
                                                                        <th>Validity</th>
                                                                        <th className="orders-table__cell--numeric">Qty</th>
                                                                        <th className="orders-table__cell--numeric">Trigger</th>
                                                                        <th className="orders-table__cell--numeric">Price</th>
                                                                        <th className="orders-table__cell--numeric">Avg</th>
                                                                        <th>Tag</th>
                                                                        <th className="orders-table__cell--numeric">Pending</th>
                                                                        <th className="orders-table__cell--numeric">Filled</th>
                                                                        <th className="orders-table__actions">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredOrders.map((order) => (
                                                                        <tr key={order.orderId}>
                                                                            <td>{order.orderId}</td>
                                                                            <td>{order.symbol || '—'}</td>
                                                                            <td>{order.status || '—'}</td>
                                                                            <td>{order.transactionType || '—'}</td>
                                                                            <td>{order.orderType || '—'}</td>
                                                                            <td>{order.validity || '—'}</td>
                                                                            <td className="orders-table__cell--numeric">
                                                                                {formatNumber(order.quantity, {
                                                                                    minimumFractionDigits: 0,
                                                                                    maximumFractionDigits: 0,
                                                                                })}
                                                                            </td>
                                                                            <td className="orders-table__cell--numeric">
                                                                                {formatNumber(order.triggerPrice, {
                                                                                    minimumFractionDigits: 2,
                                                                                    maximumFractionDigits: 2,
                                                                                })}
                                                                            </td>
                                                                            <td className="orders-table__cell--numeric">
                                                                                {formatNumber(order.price, {
                                                                                    minimumFractionDigits: 2,
                                                                                    maximumFractionDigits: 2,
                                                                                })}
                                                                            </td>
                                                                            <td className="orders-table__cell--numeric">
                                                                                {formatNumber(order.averagePrice, {
                                                                                    minimumFractionDigits: 2,
                                                                                    maximumFractionDigits: 2,
                                                                                })}
                                                                            </td>
                                                                            <td>{order.tag || '—'}</td>
                                                                            <td className="orders-table__cell--numeric">
                                                                                {formatNumber(order.pendingQuantity, {
                                                                                    minimumFractionDigits: 0,
                                                                                    maximumFractionDigits: 0,
                                                                                })}
                                                                            </td>
                                                                            <td className="orders-table__cell--numeric">
                                                                                {formatNumber(order.filledQuantity, {
                                                                                    minimumFractionDigits: 0,
                                                                                    maximumFractionDigits: 0,
                                                                                })}
                                                                            </td>
                                                                            <td className="orders-table__actions">
                                                                                {orderSupportsCancellation(order) ? (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="orders-cancel-btn orders-table__row-action"
                                                                                        onClick={() => promptCancelOrder(account, order)}
                                                                                        disabled={isProcessingCancel}
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                ) : (
                                                                                    <span className="orders-table__muted">—</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {hasGtts && (
                                                <div className="orders-card__section">
                                                    <h3 className="orders-card__subtitle">GTTs</h3>
                                                    <div className="orders-table-wrapper">
                                                        <table className="orders-table orders-table--gtt">
                                                            <thead>
                                                                <tr>
                                                                    <th>Trigger ID</th>
                                                                    <th>Symbol</th>
                                                                    <th>Status</th>
                                                                    <th>Type</th>
                                                                    <th>Triggers</th>
                                                                    <th className="orders-table__cell--numeric">Last Price</th>
                                                                    <th>Created</th>
                                                                    <th>Expires</th>
                                                                    <th>Orders</th>
                                                                    <th className="orders-table__actions">Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {account.gtts.map((gtt) => {
                                                                    const triggerValues =
                                                                        Array.isArray(gtt.triggerValues) &&
                                                                        gtt.triggerValues.length
                                                                            ? gtt.triggerValues
                                                                                  .map((value) =>
                                                                                      formatNumber(value, {
                                                                                          minimumFractionDigits: 2,
                                                                                          maximumFractionDigits: 2,
                                                                                      })
                                                                                  )
                                                                                  .join(', ')
                                                                            : '—';

                                                                    return (
                                                                        <tr key={gtt.triggerId || `${gtt.symbol}-${gtt.createdAt}`}>
                                                                            <td>{gtt.triggerId || '—'}</td>
                                                                            <td>
                                                                                <div className="orders-table__symbol">
                                                                                    <span>{gtt.symbol || '—'}</span>
                                                                                    {gtt.exchange ? (
                                                                                        <span className="orders-table__symbol-meta">
                                                                                            {gtt.exchange}
                                                                                        </span>
                                                                                    ) : null}
                                                                                </div>
                                                                            </td>
                                                                            <td>{gtt.status || '—'}</td>
                                                                            <td>{gtt.type || '—'}</td>
                                                                            <td>{triggerValues}</td>
                                                                            <td className="orders-table__cell--numeric">
                                                                                {formatNumber(gtt.lastPrice, {
                                                                                    minimumFractionDigits: 2,
                                                                                    maximumFractionDigits: 2,
                                                                                })}
                                                                            </td>
                                                                            <td>{formatDateTime(gtt.createdAt)}</td>
                                                                            <td>{formatDateTime(gtt.expireAt)}</td>
                                                                            <td>
                                                                                {Array.isArray(gtt.orders) && gtt.orders.length ? (
                                                                                    <div className="gtt-orders">
                                                                                        {gtt.orders.map((leg, index) => (
                                                                                            <div key={index} className="gtt-orders__leg">
                                                                                                <span>{leg.transactionType || '—'}</span>
                                                                                                <span>{leg.orderType || '—'}</span>
                                                                                                <span>{leg.product || '—'}</span>
                                                                                                <span>
                                                                                                    Qty{' '}
                                                                                                    {formatNumber(leg.quantity, {
                                                                                                        minimumFractionDigits: 0,
                                                                                                        maximumFractionDigits: 0,
                                                                                                    })}
                                                                                                </span>
                                                                                                <span>
                                                                                                    Price{' '}
                                                                                                    {formatNumber(leg.price, {
                                                                                                        minimumFractionDigits: 2,
                                                                                                        maximumFractionDigits: 2,
                                                                                                    })}
                                                                                                </span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    '—'
                                                                                )}
                                                                            </td>
                                                                            <td className="orders-table__actions">
                                                                                {gttSupportsCancellation(gtt) ? (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="orders-cancel-btn orders-table__row-action"
                                                                                        onClick={() => promptCancelGtt(account, gtt)}
                                                                                        disabled={isProcessingCancel}
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                ) : (
                                                                                    <span className="orders-table__muted">—</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                            {!hasOrders && !hasGtts && (
                                                <div className="orders-card__message">No orders or GTTs for this account.</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="orders-card orders-card--message">No orders or GTTs found today.</div>
                    )}
                </div>
            </GlassPaper>
            {cancelDialog && (
                <div className="cancel-dialog-backdrop" role="presentation" onClick={closeCancelDialog}>
                    <GlassPaper
                        className="cancel-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="cancel-dialog-title"
                        component="div"
                        elevation={0}
                        square={false}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h3 id="cancel-dialog-title" className="cancel-dialog__title">
                            {cancelDialog.title}
                        </h3>
                        <p className="cancel-dialog__message">{cancelDialog.message}</p>
                        <div className="cancel-dialog__actions">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={closeCancelDialog}
                                disabled={isProcessingCancel}
                            >
                                No
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleConfirmCancellation}
                                disabled={isProcessingCancel}
                            >
                                {isProcessingCancel ? 'Cancelling…' : 'Yes, Cancel'}
                            </button>
                        </div>
                    </GlassPaper>
                </div>
            )}
        </div>
    );
};

export default TradingHubPage;
